import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  BoxProps,
  Button,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import * as R from "ramda";
import React, { useEffect, useState } from "react";
import { useFetchPushConnectorListener, usePushConnector } from "../../../hook/pushConnector";
import { PushConnectorBlock } from "../../../types/notebook/note/block/pushConnector";
import {
  PushConnectorFormField,
  PushConnectorObject,
} from "../../../types/pushConnector";
import { NoteBlockContainer } from "../../note";
import RemoveIcon from "@mui/icons-material/Remove";

export const PushConectorFormFieldComponent: React.FC<
  {
    field: PushConnectorFormField;
    index: number;
    setValue: (index: number, newValue: string) => void;
    setLabel: (index: number, newLabel: string) => void;
    onRemoveField: (index: number) => void;
  } & BoxProps
> = ({
  field: { label, value },
  index: i,
  setValue,
  setLabel,
  onRemoveField,
  ...boxProps
}) => {
  const reservedLabels = ["name", "description", "prompt"];
  const isReservedLabel = reservedLabels.includes(label);

  return (
    <Box
      {...boxProps}
      style={{
        display: "flex",
        gap: "1rem",
        flexGrow: 1,
      }}
    >
      <TextField
        label="Label"
        disabled={isReservedLabel}
        sx={{ flexGrow: 1 }}
        value={label}
        onChange={(e) => {
          setLabel(i, e.target.value);
        }}
      />
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          gap: "1rem",
          alignItems: "flex-start",
        }}
      >
        <TextField
          label="Value"
          multiline={true}
          sx={{ flexGrow: 1 }}
          value={value}
          onChange={(e) => {
            setValue(i, e.target.value);
          }}
        />
        <IconButton
          color="primary"
          disabled={isReservedLabel}
          onClick={() => onRemoveField(i)}
        >
          <RemoveIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export const PushConectorForm: React.FC<{
  pushConnectorId: string | undefined;
}> = ({ pushConnectorId }) => {
  const { setPushConnector, pushConnector: pushCon } =
    usePushConnector(pushConnectorId);
  const pushConDefined = pushCon !== undefined;
  const [formLocal, setFormLocal] = useState<PushConnectorFormField[]>([]);

  const setFormFromState = () => {
    if (!pushCon) return;
    const spec = pushCon["push-con-spec"];
    const entries = Object.entries(spec).map(([key, value]) => ({
      label: key,
      value: value as string,
    }));
    setFormLocal(entries);
  };

  useEffect(() => {
    setFormFromState();
  }, [pushConnectorId, pushConDefined]);
  const name = formLocal.find((field) => field.label === "name")?.value || "";

  const setLabel = (index: number, newLabel: string) => {
    setFormLocal((prev) => {
      const existingElem = formLocal[index];
      const newElem = { ...existingElem, label: newLabel };
      const newForm = R.update(index, newElem, prev);
      return newForm;
    });
  };

  const setValue = (index: number, newValue: string) => {
    setFormLocal((prev) => {
      const existingElem = formLocal[index];
      const newElem = { ...existingElem, value: newValue };
      const newForm = R.update(index, newElem, prev);
      return newForm;
    });
  };

  const onSave = () => {
    if (!pushCon) return;
    const newPushConSpecEntries = formLocal.map(({ label, value }) => [
      label,
      value,
    ]);
    const newPushConSpec = Object.fromEntries(newPushConSpecEntries);
    const newPushCon: PushConnectorObject = {
      ...pushCon,
      "push-con-spec": newPushConSpec,
    };
    setPushConnector(newPushCon);
  };

  const onCancel = () => {
    setFormFromState();
  };

  const onRemoveField = (index: number) => {
    setFormLocal((prev) => {
      const newForm = R.remove(index, 1, prev);
      return newForm;
    });
  };

  const onAddField = () => {
    setFormLocal((prev) => {
      const newForm = [...prev, { label: "", value: "" }];
      return newForm;
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography>{name}</Typography>
      {formLocal.map((formField, i) => {
        return (
          <PushConectorFormFieldComponent
            key={i}
            setLabel={setLabel}
            setValue={setValue}
            onRemoveField={onRemoveField}
            field={formField}
            index={i}
          />
        );
      })}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <IconButton
          color="primary"
          onClick={() => {
            onAddField();
          }}
        >
          <AddIcon />
        </IconButton>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button onClick={() => onSave()}>Save</Button>
        <Button onClick={() => onCancel()}>Cancel</Button>
      </Box>
    </Box>
  );
};

export const NotePageBlockPushConnector: React.FC<{
  block: PushConnectorBlock;
}> = React.memo(({ block }) => {
  const { "push-con-id": pushConId } = block;
  useFetchPushConnectorListener(pushConId);

  return (
    <NoteBlockContainer name="Credential">
      <PushConectorForm pushConnectorId={pushConId} />
    </NoteBlockContainer>
  );
});
